// Returns a new array sorted: headliner(s) first (is_headliner === true),
// then all others — all groups sub-sorted by position asc, then id asc.
//
// `is_headliner` is the canonical boolean from the v3_offer blueprint and
// already encodes whatever rules the backend wants (act_type === 'headline'
// or otherwise). Callers should not parse `act_type` themselves.
export function sortLineup(lineup) {
  return [...lineup].sort((a, b) => {
    const aIsHead = a.is_headliner ? 0 : 1;
    const bIsHead = b.is_headliner ? 0 : 1;
    if (aIsHead !== bIsHead) return aIsHead - bIsHead;
    if (a.position !== b.position) return a.position - b.position;
    return a.id - b.id;
  });
}

// Returns [headliner | null, supports[]] tuple.
// supports[] is sorted via sortLineup (excludes the headliner).
export function splitHeadliner(lineup) {
  const sorted = sortLineup(lineup);
  if (sorted.length > 0 && sorted[0].is_headliner) {
    return [sorted[0], sorted.slice(1)];
  }
  return [null, sorted];
}

// O-5024: is this the offer that speaks for the show — the 1st headliner?
//
// PREFERS the server's own answer (Internal::V3OfferBlueprint#primary_for_show).
// NOT lineup_primary?, which asks a different question and is FALSE on a solo
// show — reading that here made every brand-new offer look like a support, so
// the picker offered it saved offer templates instead of event templates.
// The local derivation below reads `offer.lineup`, which is built for the lineup
// STRIP: it prefers calendar_event siblings and applies no team filter, while
// V3Offer#lineup_primary? keys on budget siblings with one. A pooled
// copy_for(keep_calendar_event: true) off a Hold makes them disagree.
//
// The fallback stays for offers that were never serialized through that view —
// lineup rows and bare test renders — where "no lineup means its own primary"
// is still the right reading.
export function isPrimaryOffer(offer) {
  if (typeof offer?.primary_for_show === 'boolean') return offer.primary_for_show;

  const lineup = offer?.lineup || [];
  if (lineup.length === 0) return true;
  return sortLineup(lineup)[0]?.id === offer?.id;
}

// O-5024: would the server accept an OFFER template — a blueprint, or a saved
// one — on this offer? Again the server's own answer wins
// (Internal::V3OfferBlueprint#can_apply_offer_template), which is
// !V3Offer#lineup_primary?: it refuses only the headliner of a REAL lineup,
// whose deal is the show's and comes from an event template. A show with one act
// draws no such distinction, which is why every brand-new offer can still start
// from a blueprint.
//
// Deliberately not the same question as !isPrimaryOffer: that decides whether to
// show a per-offer control, and says no on a solo show. This says whether the
// apply would succeed, and says yes.
export function canApplyOfferTemplate(offer) {
  if (typeof offer?.can_apply_offer_template === 'boolean') return offer.can_apply_offer_template;

  if ((offer?.lineup || []).length <= 1) return true;
  return !isPrimaryOffer(offer);
}

// Pure array reorder: move the entry at fromIdx to toIdx.
// Returns a new array; does not mutate input.
export function reorderSupports(supports, fromIdx, toIdx) {
  const result = [...supports];
  const [item] = result.splice(fromIdx, 1);
  result.splice(toIdx, 0, item);
  return result;
}
